import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ExtraCostline } from "../models/extracostline";
import { PaymentMethod } from "../models/paymentmethod";
import { ArticleLine } from "../models/articleline";
import { ConfigService } from "./config.service";
import { Product } from "../models/product";
import { is_filled_in } from "../misc/functions";
import { ToastrService } from "ngx-toastr";
import { TextService } from "./text.service";
import { CartStore } from "../store/cart.store";

export class OrderResponse {
  transaction_guid: string;
  success: boolean;
  message: string;
}

export class PaymentResponse {
  url: string;
}

@Injectable({
  providedIn: "root",
})
export class OrderService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private toastrService = inject(ToastrService);
  private textService = inject(TextService);
  private cartStore = inject(CartStore);

  public createOrder(email: string, name: string): Observable<OrderResponse> {
    const products = this.cartStore.articles();

    const requestData = {
      products: [],
      table_id: this.getTableId(),
      payment_method: this.getPaymentMethod(),
      email: email,
      name: name,
    };

    if (!requestData.table_id || !is_filled_in(requestData.table_id)) {
      console.error(requestData, products, "Table not found");
      this.textService.get("products.table_error").subscribe((text) => {
        this.toastrService.error(text);
      });
      return;
    }

    for (const product of products) {
      if (+product.quantity <= 0) {
        continue;
      }

      requestData.products.push({
        product_id: product.product.id,
        quantity: product.quantity,
        questionDetails: Object.values(product.questionDetails),
      });
    }

    for (const item of this.getExtraCosts()) {
      requestData.products.push({
        product_id: item.articleId,
        price: item.price,
        quantity: 1,
        questionDetails: [],
      });
    }

    return this.http
      .post(this.configService.getApiUrl() + "/order", requestData)
      .pipe(
        map((response) => {
          return response as OrderResponse;
        }),
      );
  }

  public createPayment(
    transactionGuid: string,
    paymentMethod: string,
  ): Observable<PaymentResponse> {
    const url =
      this.configService.getApiUrl() +
      "/payment/" +
      transactionGuid +
      "/" +
      paymentMethod;
    return this.http.get(url).pipe(
      map((response) => {
        return response as PaymentResponse;
      }),
    );
  }

  getTransactionGuid(): string {
    return sessionStorage.getItem("guid");
  }

  setTransactionGuid(transaction_guid: string) {
    sessionStorage.setItem("guid", transaction_guid);
  }

  public getProductTotal(): number {
    let productTotal = 0;

    for (const currentArticle of this.cartStore.articles()) {
      const quantity = +currentArticle.quantity;
      const productPrice = +currentArticle.price;
      const price = productPrice * quantity;

      for (const currentOptionId in currentArticle.questionDetails) {
        const currentOption = currentArticle.questionDetails[currentOptionId];
        productTotal += +currentOption.price * quantity;
      }
      productTotal += price;
    }

    return productTotal;
  }

  getPaymentMethod(): string {
    return sessionStorage.getItem("payment_method");
  }

  getExtraCosts(): ExtraCostline[] {
    const lines = JSON.parse(
      sessionStorage.getItem("extracostlines"),
    ) as ExtraCostline[];

    if (lines == null) {
      return [];
    }

    return lines;
  }

  updateTip(price: number, articleId: string) {
    const currentCosts = this.getExtraCosts();

    // eslint-disable-next-line guard-for-in
    for (const lineKey in currentCosts) {
      const line = currentCosts[lineKey];
      if (line.type === "tip") {
        currentCosts.splice(Number(lineKey), 1);
      }
    }

    const newCost = {
      type: "tip",
      price,
      description: "Tip",
      articleId: articleId,
      id: articleId,
    } as ExtraCostline;

    sessionStorage.setItem("tip", price.toString());

    currentCosts.push(newCost);
    sessionStorage.setItem("extracostlines", JSON.stringify(currentCosts));
  }

  updatePaymentCosts(paymentMethod: PaymentMethod, price: number) {
    const currentCosts = this.getExtraCosts();

    // eslint-disable-next-line guard-for-in
    for (const lineKey in currentCosts) {
      const line = currentCosts[lineKey];
      if (line.type === "payment_method") {
        currentCosts.splice(Number(lineKey), 1);
      }
    }

    const newCost = {
      type: "payment_method",
      price,
      description: paymentMethod.name,
      articleId: paymentMethod.articleId,
      id: paymentMethod.id,
    } as ExtraCostline;

    sessionStorage.setItem("payment_method", paymentMethod.id);

    currentCosts.push(newCost);
    sessionStorage.setItem("extracostlines", JSON.stringify(currentCosts));
  }

  addProduct(product: Product, questionDetails: ArticleLine["questionDetails"] = {}) {
    const article = new ArticleLine();
    article.generatedId =
      product.id +
      Date.now().toString(36) +
      Math.random().toString(36).substr(2);
    article.product = product;
    article.price = +product.price;
    article.quantity = 1;
    article.questionDetails = questionDetails;
    this.cartStore.addArticle(article);
  }

  removeArticleByGeneratedId(id: string) {
    this.cartStore.decrementArticleByGeneratedId(id);
  }

  removeArticleByProductId(id: string) {
    this.cartStore.decrementArticleByProductId(id);
  }

  addArticleByGeneratedId(id: string) {
    this.cartStore.incrementArticle(id);
  }

  getGrandTotal(): number {
    let grandTotal = 0.0;

    grandTotal += this.getProductTotal();

    for (const extraCost of this.getExtraCosts()) {
      grandTotal += extraCost.price;
    }

    return grandTotal;
  }

  setTableId(id: string) {
    sessionStorage.setItem("tableId", id);
  }

  getTableId(): string {
    return sessionStorage.getItem("tableId");
  }

  removeExtraCosts() {
    sessionStorage.setItem("extracostlines", JSON.stringify([]));
  }
}
